'use client'

import { gql, useQuery } from '@apollo/client'
import {
  MultiSelector,
  MultiSelectorContent,
  MultiSelectorInput,
  MultiSelectorItem,
  MultiSelectorList,
  MultiSelectorTrigger,
} from '@/components/ui/multi-selector'
import { useLanguage } from '@/context/LanguageContext'

const GET_PRODUCTS = gql`
  query GET_PRODUCTS($language: Language!) {
    findManyProductContainer {
      id
      single_product {
        id
        product_profiles(where: { language: { equals: $language } }) {
          language
          name
        }
        images {
          src_thumbnail
        }
      }
      advanced_product {
        advanced_product_profiles(where: { language: { equals: $language } }) {
          name
          language
        }
        images {
          src_thumbnail
        }
      }
    }
  }
`

interface ProductSelectProps {
  field: {
    value: Array<{ id: number }>
    onChange: (value: Array<{ id: number }>) => void
  }
}

interface Product {
  id: string
  name: string
  image?: string
}

type ProductValue = {
  id: string
  name: string
  image?: string
}

export default function ProductSelect({ field }: ProductSelectProps) {
  const { language } = useLanguage()
  const { data, loading, error } = useQuery(GET_PRODUCTS, {
    variables: { language },
  })

  if (loading) return <div>Loading...</div>
  if (error) return <div>Error loading products</div>

  const products: Product[] = (data?.findManyProductContainer || []).map(
    (product: any): Product => {
      if (product.single_product) {
        // Get profile for current language, fallback to first available
        const profile =
          product.single_product?.product_profiles?.find(
            (p: any) => p.language === language
          ) || product.single_product?.product_profiles?.[0]

        return {
          id: product.id,
          name: profile?.name || 'Unnamed Product',
          image: product.single_product?.images?.[0]?.src_thumbnail,
        }
      } else {
        // Get profile for current language, fallback to first available
        const profile =
          product.advanced_product?.advanced_product_profiles?.find(
            (p: any) => p.language === language
          ) || product.advanced_product?.advanced_product_profiles?.[0]

        return {
          id: product.id,
          name: profile?.name || 'Unnamed Product',
          image: product.advanced_product?.images?.[0]?.src_thumbnail,
        }
      }
    }
  )

  const selectedValues = (field.value ?? [])
    .map(item => {
      const product = data?.findManyProductContainer.find(
        (p: any) => p.id === item.id
      )
      if (!product) return null

      let name, image
      if (product.single_product) {
        // Get profile for current language, fallback to first available
        const profile =
          product.single_product?.product_profiles?.find(
            (p: any) => p.language === language
          ) || product.single_product?.product_profiles?.[0]
        name = profile?.name
        image = product.single_product.images?.[0]?.src_thumbnail
      } else {
        // Get profile for current language, fallback to first available
        const profile =
          product.advanced_product?.advanced_product_profiles?.find(
            (p: any) => p.language === language
          ) || product.advanced_product?.advanced_product_profiles?.[0]
        name = profile?.name
        image = product.advanced_product?.images?.[0]?.src_thumbnail
      }

      return {
        id: product.id.toString(),
        name: name || 'Unnamed Product',
        image,
      } as const
    })
    .filter((value): value is NonNullable<typeof value> => value !== null)

  return (
    <MultiSelector
      values={selectedValues}
      onValuesChange={values =>
        field.onChange(values.map(value => ({ id: parseInt(value.id) })))
      }
      loop
      keepInputOnSelect
      tableView
      className="max-w-full"
    >
      <MultiSelectorTrigger>
        <MultiSelectorInput placeholder="Select products..." />
      </MultiSelectorTrigger>
      <MultiSelectorContent>
        <MultiSelectorList>
          {products.map(product => (
            <MultiSelectorItem
              key={product.id}
              value={{
                id: product.id.toString(),
                name: product.name || 'Unnamed Product',
                image: product.image,
              }}
              className="flex items-center gap-2 w-full"
            >
              <div className="flex items-center gap-2 min-w-[40px]">
                {product.image && (
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-8 h-8 object-cover rounded"
                  />
                )}
              </div>
              <span className="flex-1 text-left">
                {product.name || 'Unnamed Product'}
              </span>
            </MultiSelectorItem>
          ))}
        </MultiSelectorList>
      </MultiSelectorContent>
    </MultiSelector>
  )
}
